// src/vendor/UserSettings/components/InvoiceList.jsx
import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { TextField, InputAdornment } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import TableListing from "../../../components/TableListing";
import InvoiceDetailsDialog from "./InvoiceDetailsDialog";
import useAuth from "../../../hooks/useAuth";
import { getInvoices, getInvoice } from "../api";

const useStyles = makeStyles((theme) => ({
  searchBar: {
    background: theme.palette.common.white,
    margin: theme.spacing(0.5, 0),
    [theme.breakpoints.down("sm")]: {
      flex: 1,
    },
  },
  row: {
    width: "100%",
    display: "flex",
    margin: theme.spacing(1, 0),
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    alignItems: "center",
  },
}));

const InvoiceList = () => {
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState("");
  const [pageNum, setPageNum] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("fees_id");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { loading } = useAuth();

  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (!loading) {
      getInvoices().then((res) => {
        // console.log(res);
        setInvoices(res.data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handlePayInvoice = (invoice) => {
    getInvoice(invoice.fees_id)
      .then((res) => {
        const invoice = res.data.invoice;
        setInvoices((invoices) =>
          invoices.map((inv) => (inv.fees_id === invoice.fees_id ? invoice : inv)),
        );
        if (invoice.status !== "paid") {
          window.location.href = res.data.url;
        }
      })
      .finally(() => {
        setDialogOpen(false);
      });
  };

  // Filter invoices based on search
  const filteredInvoices = invoices.filter(
    (invoice) =>
      (invoice.provider_id || invoice.fees_id)
        .toString()
        .toLowerCase()
        .includes(searchValue.toLowerCase()) ||
      invoice.description.toLowerCase().includes(searchValue.toLowerCase()),
  );

  const headCells = [
    {
      id: "description",
      numeric: false,
      disablePadding: false,
      label: "Description",
      sort: true,
      width: "30%",
    },
    {
      id: "amount",
      numeric: true,
      disablePadding: false,
      label: "Amount",
      sort: true,
      width: "20%",
      isFunction: {
        function: (invoice) => `$${invoice.amount}`,
      },
    },
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      label: "Status",
      sort: true,
      width: "20%",
    },
    {
      id: "provider_id",
      numeric: false,
      disablePadding: true,
      label: "Invoice ID",
      sort: true,
      width: "30%",
    },
  ];

  // Calculate pagination
  const startIndex = pageNum * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  const handleRowClick = (row) => {
    setSelectedInvoice(row);
    setDialogOpen(true);
  };

  return (
    <>
      <div className={classes.row}>
        <TextField
          variant="outlined"
          margin="dense"
          placeholder="Search invoices..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="disabled" />
              </InputAdornment>
            ),
          }}
          className={classes.searchBar}
          size="small"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <TableListing
          headCells={headCells}
          rows={paginatedInvoices}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          pageSize={pageSize}
          setPageSize={(size) => {
            setPageSize(size);
            setPageNum(0);
          }}
          pageNum={pageNum}
          setPageNum={setPageNum}
          totalCount={filteredInvoices.length}
          variable="Invoices"
          loading={false}
          hover={true}
          onRowClick={handleRowClick}
        />
      </div>

      <InvoiceDetailsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        invoice={selectedInvoice}
        handlePayInvoice={handlePayInvoice}
      />
    </>
  );
};

export default InvoiceList;
